import ChargerModule from '../../components/chargerModule';
import { SwitchText, SwitchStatus } from '../../views/business/BusinessListing/components/SwitchText/SwitchText';
import NudosTextInput from '../../components/NudosComponents/NudosTextInput/NudosTextInput';
import NudosTextArea from '../../components/NudosComponents/NudosTextArea/NudosTextArea';
import BannerColorSelector, { IBannerColor } from '../../components/BannerColorSelector/BannerColorSelector';
import BordModalConfirm from '../../components/NudosComponents/BordModalConfirm/BordModalConfirm';
import { NudosButton } from 'components/NudosComponents';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { bordColorTheme } from 'styles/styles.constants';
import './Announcements.scss';
import { BACKOFFICE_MODULES } from 'types/configuration.types';

const ANNOUNCEMENT_COLORS: IBannerColor[] = [
  { id: 'green', color: bordColorTheme.green[600], label: 'Verde' },
  { id: 'blue', color: bordColorTheme.blue[400], label: 'Azul' },
  { id: 'red', color: bordColorTheme.redBright[500], label: 'Rojo' },
  { id: 'yellow', color: bordColorTheme.yellow[700], label: 'Amarillo' }
];

const Announcements = () => {
  const {
    subject,
    bannerText,
    bannerTextEn,
    isEditMode,
    showUnsavedChangesModal,
    showSaveBannerModal,
    showDeactivateModal,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    setSubject,
    handleBannerTextChange,
    handleBannerTextEnChange,
    handleColorSelect,
    hasSubjectError,
    hasBannerTextError,
    hasBannerTextEnError,
    isFormValid,
    handleToggleChange,
    handleSaveBanner,
    handleConfirmSaveBanner,
    handleCancelSaveBanner,
    handleConfirmDeactivate,
    handleCancelDeactivate,
    handleConfirmExit,
    handleCancelExit,
    getSelectedColorId,
    MAX_TEXT_LENGTH,
    MAX_SUBJECT_LENGTH
  } = useAnnouncements({ colors: ANNOUNCEMENT_COLORS });

  const headerInfo = {
    title: 'Anuncios',
    subtitle: ''
  };

  const infoSideBar = { position: BACKOFFICE_MODULES.ANNOUNCEMENT };
  const isDeactivated = !isEditMode || isLoading;
  const isButtonDisabled = isDeactivated || isSaving || !isFormValid() || !hasUnsavedChanges;

  return (
    <ChargerModule headerInfo={headerInfo} infoSideBar={infoSideBar}>
      <div className="announcements">
        <div className="announcementsHeader">
          <h2 className="announcementsTitle">Banner informativo</h2>
          <SwitchText
            status={isEditMode ? SwitchStatus.ACTIVE : SwitchStatus.INACTIVE}
            handleChangeStatus={handleToggleChange}
            activeLabel="en uso"
            inactiveLabel="inactivo"
            isDisabled={isLoading}
          />
        </div>
        <div className="divider" />
        <div className="announcementsContent">
          <div className="announcementsTextAreaWrapper">
            <NudosTextInput
              label="Asunto del anuncio (no se mostrará en el banner de Dash)"
              placeholder="Ingresa el asunto del anuncio"
              defaultValue={subject}
              handleChange={(value: string) => setSubject(value)}
              isFilled={!!subject}
              isDeactivated={isDeactivated}
              componentSize="large"
              errorText={hasSubjectError() ? 'error' : undefined}
              hideErrorText={true}
            />
            <div className="announcementsSubjectFooter">
              {hasSubjectError() && (
                <div className="announcementsSubjectErrorMessage">
                  Has excedido el límite de {MAX_SUBJECT_LENGTH} caracteres
                </div>
              )}
            </div>
          </div>

          <div className="announcementsTextAreaWrapper">
            <NudosTextArea
              label="Texto del banner (Español)"
              placeholder="Ingresa el mensaje que quieres mostrar en el banner en español"
              currentValue={bannerText}
              setCurrentValue={handleBannerTextChange}
              maxLength={MAX_TEXT_LENGTH}
              textHeight={80}
              isDeactivated={isDeactivated}
              hidenCounter={false}
              noResize
              customClassName="announcementsTextArea"
              customLabelClassName={`announcementsTextAreaLabel ${isDeactivated ? 'deactivated' : ''}`}
              customCounterClassName={`announcementsTextAreaCounter ${isDeactivated ? 'deactivated' : ''}`}
              xPadding="15"
              yPadding="15"
            />
            <div className="announcementsTextAreaFooter">
              {hasBannerTextError() && (
                <div className="announcementsErrorMessage">Has excedido el límite de {MAX_TEXT_LENGTH} caracteres</div>
              )}
            </div>
          </div>

          <div className="announcementsTextAreaWrapper">
            <NudosTextArea
              label="Texto del banner (Inglés)"
              placeholder="Ingresa el mensaje que quieres mostrar en el banner en inglés (opcional)"
              currentValue={bannerTextEn}
              setCurrentValue={handleBannerTextEnChange}
              maxLength={MAX_TEXT_LENGTH}
              textHeight={80}
              isDeactivated={isDeactivated}
              hidenCounter={false}
              noResize
              customClassName="announcementsTextArea"
              customLabelClassName={`announcementsTextAreaLabel ${isDeactivated ? 'deactivated' : ''}`}
              customCounterClassName={`announcementsTextAreaCounter ${isDeactivated ? 'deactivated' : ''}`}
              xPadding="15"
              yPadding="15"
            />
            <div className="announcementsTextAreaFooter">
              {hasBannerTextEnError() && (
                <div className="announcementsErrorMessage">Has excedido el límite de {MAX_TEXT_LENGTH} caracteres</div>
              )}
            </div>
          </div>

          <BannerColorSelector
            selectedColorId={getSelectedColorId()}
            onColorSelect={handleColorSelect}
            colors={ANNOUNCEMENT_COLORS}
            isDisabled={isDeactivated}
          />

          <div className="announcementsActions">
            <NudosButton
              customClassName="saveBannerButton"
              handleClick={handleSaveBanner}
              isButtonDisabled={isButtonDisabled}
              isButtonLoading={isSaving}
              buttonText="Guardar banner"
            />
          </div>
        </div>

        {showSaveBannerModal && (
          <BordModalConfirm
            onAccept={handleConfirmSaveBanner}
            onCancel={handleCancelSaveBanner}
            isLoading={isSaving}
            isDisabled={isSaving}
            title="¿Estás seguro que deseas guardar este banner? Será visible para todos los usuarios en Dash.bord una vez publicado"
            textButtonAccept="Confirmar"
            textButtonCancel="Cancelar"
            clickOutsideCallback={handleCancelSaveBanner}
            closeModalCallback={handleCancelSaveBanner}
          />
        )}

        {showDeactivateModal && (
          <BordModalConfirm
            onAccept={handleConfirmDeactivate}
            onCancel={handleCancelDeactivate}
            isLoading={isLoading}
            isDisabled={isLoading}
            title="¿Estás seguro que deseas desactivar este banner? Ya no será visible para los usuarios en Dash.bord"
            textButtonAccept="Desactivar"
            textButtonCancel="Cancelar"
            clickOutsideCallback={handleCancelDeactivate}
            closeModalCallback={handleCancelDeactivate}
          />
        )}

        {showUnsavedChangesModal && (
          <BordModalConfirm
            onAccept={handleConfirmExit}
            onCancel={handleCancelExit}
            isLoading={false}
            isDisabled={false}
            title="¿Estás seguro que deseas salir sin guardar? Los cambios realizados en el banner se perderán y no se mostrarán en Dash.bord"
            textButtonAccept="Salir sin guardar"
            textButtonCancel="Cancelar"
            clickOutsideCallback={handleCancelExit}
            closeModalCallback={handleCancelExit}
          />
        )}
      </div>
    </ChargerModule>
  );
};

export default Announcements;
